import { CommercialOfferActions, GENERATE_COMMERCIAL_OFFER } from 'models/CommercialOffer';

export enum CommercialOfferStatus {
  DRAFT = 'DRAFT',
  COMMERCIAL_OFFER_GENERATED = 'COMMERCIAL_OFFER_GENERATED',
  NEGOTIATE_FIRST_CALL = 'NEGOTIATE_FIRST_CALL',
  NEGOTIATION = 'NEGOTIATION',
  PRICE_CONFIRMATION_REQUESTED = 'PRICE_CONFIRMATION_REQUESTED',
  PRICE_CONFIRMED = 'PRICE_CONFIRMED',
  PRE_INVOICE_SENT = 'PRE_INVOICE_SENT',
  ORDER_CONFIRMATION_SENT = 'ORDER_CONFIRMATION_SENT',
  REJECTED = 'REJECTED',
}

// Map display names for statuses
export const statusDisplayNames: Record<string, string> = {
  [CommercialOfferStatus.DRAFT]: 'Draft',
  [CommercialOfferStatus.COMMERCIAL_OFFER_GENERATED]: 'Commercial Offer Generated',
  [CommercialOfferStatus.NEGOTIATE_FIRST_CALL]: 'Negotiate: First Call',
  [CommercialOfferStatus.NEGOTIATION]: 'Negotiation',
  [CommercialOfferStatus.PRICE_CONFIRMATION_REQUESTED]: 'Price Confirmation Requested',
  [CommercialOfferStatus.PRICE_CONFIRMED]: 'Price Confirmed',
  [CommercialOfferStatus.PRE_INVOICE_SENT]: 'Pre-Invoice Sent, Pending Payment',
  [CommercialOfferStatus.ORDER_CONFIRMATION_SENT]: 'Order Confirmation Sent',
  [CommercialOfferStatus.REJECTED]: 'Rejected',
};

// Helper function to check if a status is rejected
export const isRejected = (status: string): boolean => {
  return status?.toUpperCase() === CommercialOfferStatus.REJECTED;
};

// Helper function to get available actions based on status
export const getAvailableActions = (status: string, paymentTerms?: string): string[] => {
  const normalizedStatus = status?.toUpperCase();

  // Reject is always available for all statuses except REJECTED
  if (isRejected(status)) {
    return []; // No actions available for rejected offers
  }

  // Start with standard actions from the CommercialOfferActions enum
  let availableActions: string[] = [CommercialOfferActions.REJECT.value];

  switch (normalizedStatus) {
    case CommercialOfferStatus.DRAFT:
      // Add our custom action for Draft status
      availableActions.push(GENERATE_COMMERCIAL_OFFER);
      break;

    case CommercialOfferStatus.COMMERCIAL_OFFER_GENERATED:
      availableActions.push(CommercialOfferActions.SEND_COMMERCIAL_OFFER.value);
      break;

    case CommercialOfferStatus.NEGOTIATE_FIRST_CALL:
      availableActions.push(
        CommercialOfferActions.NEGOTIATE.value,
        CommercialOfferActions.REQUEST_PRICE_CONFIRMATION.value
      );
      break;

    case CommercialOfferStatus.NEGOTIATION:
      availableActions.push(
        CommercialOfferActions.NEGOTIATE.value,
        CommercialOfferActions.REQUEST_PRICE_CONFIRMATION.value
      );
      break;

    case CommercialOfferStatus.PRICE_CONFIRMED:
      // Add actions based on payment terms
      if (paymentTerms === 'PREPAID' || paymentTerms === 'INSTALLMENTS') {
        availableActions.push(CommercialOfferActions.SEND_PRE_INVOICE.value);
      }

      if (paymentTerms === 'POST_PAID') {
        availableActions.push(CommercialOfferActions.SEND_ORDER_CONFIRMATION.value);
      }
      break;
    case CommercialOfferStatus.PRE_INVOICE_SENT:
    case CommercialOfferStatus.ORDER_CONFIRMATION_SENT:
      availableActions = availableActions.filter((action: string) => action !== CommercialOfferActions.REJECT.value);
      break;
  }

  return availableActions;
};
export const getAvailableSendActions = (status: string, paymentTerms?: string): string[] => {
  const normalizedStatus = status?.toUpperCase();

  // Reject is always available for all statuses except REJECTED
  if (isRejected(status)) {
    return []; // No actions available for rejected offers
  }

  // Start with standard actions from the CommercialOfferActions enum
  const availableActions: string[] = [];

  switch (normalizedStatus) {
    case CommercialOfferStatus.COMMERCIAL_OFFER_GENERATED:
      availableActions.push(CommercialOfferActions.SEND_COMMERCIAL_OFFER.value);
      break;
    case CommercialOfferStatus.PRICE_CONFIRMED:
      // Add actions based on payment terms
      if (paymentTerms === 'PREPAID' || paymentTerms === 'INSTALLMENTS') {
        availableActions.push(CommercialOfferActions.SEND_PRE_INVOICE.value);
      }

      if (paymentTerms === 'POST_PAID') {
        availableActions.push(CommercialOfferActions.SEND_ORDER_CONFIRMATION.value);
      }
      break;
  }

  return availableActions;
};

// Helper function to check if a commercial offer is editable
export const isOfferEditable = (status: string): boolean => {
  const normalizedStatus = status?.toUpperCase();
  return [CommercialOfferStatus.DRAFT, CommercialOfferStatus.COMMERCIAL_OFFER_GENERATED].includes(
    normalizedStatus as CommercialOfferStatus
  );
};

// Helper function to get the status tag color
export const getStatusTagColor = (status: string): string => {
  const normalizedStatus = status?.toUpperCase();

  switch (normalizedStatus) {
    case CommercialOfferStatus.DRAFT:
      return 'default';
    case CommercialOfferStatus.COMMERCIAL_OFFER_GENERATED:
      return 'blue';
    case CommercialOfferStatus.NEGOTIATE_FIRST_CALL:
    case CommercialOfferStatus.NEGOTIATION:
      return 'processing';
    case CommercialOfferStatus.PRICE_CONFIRMATION_REQUESTED:
      return 'warning';
    case CommercialOfferStatus.PRICE_CONFIRMED:
      return 'success';
    case CommercialOfferStatus.PRE_INVOICE_SENT:
    case CommercialOfferStatus.ORDER_CONFIRMATION_SENT:
      return 'green';
    case CommercialOfferStatus.REJECTED:
      return 'error';
    default:
      return 'default';
  }
};
